import { useTranslation } from 'react-i18next';
import type { DailyRouteActivity } from '@apis/reports';
import { CardIcon } from '@components/Pages/AnalyticsPage/CardIcon';
import { DataRow } from './DataRow/DataRow';
import styles from './ActivitiesSection.module.css';

interface IActivitiesSectionProps {
  activities: DailyRouteActivity[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /**
   * i18n key prefix for the section's strings (`.section.activities`,
   * `.empty.activities`, `.errorRetry`, `.retry`). Defaults to the Daily
   * Route namespace so DR call sites stay zero-config; Incident Report
   * passes `'reports.incidentReport'`.
   */
  i18nNamespace?: string;
}

export function ActivitiesSection({
  activities,
  isLoading,
  isError,
  onRetry,
  i18nNamespace = 'reports.dailyRoute',
}: IActivitiesSectionProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div
        className={styles.skeleton}
        role="status"
        aria-busy="true"
        aria-label={t(`${i18nNamespace}.section.activities`)}
      >
        …
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.error} role="alert">
        <span className={styles.errorMessage}>{t(`${i18nNamespace}.errorRetry`)}</span>
        <button type="button" className={styles.retry} onClick={onRetry}>
          {t(`${i18nNamespace}.retry`)}
        </button>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div role="status" className={styles.empty}>
        <CardIcon name="clipboard" size={16} />
        <span>{t(`${i18nNamespace}.empty.activities`)}</span>
      </div>
    );
  }

  return (
    <ul className={styles.list}>
      {activities.map((a) => (
        <DataRow
          key={a.activityId}
          label={a.name}
          chip={{ text: a.professionName, backgroundColor: a.professionColor }}
        />
      ))}
    </ul>
  );
}
